/**
 * AdminLogin.js
 * Login page specifically for platform administrators
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import WingmanLogo from '../components/WingmanLogo';
import colors from '../utils/adminColors';
import { API_BASE } from '../config';

// Lane 1416: short machine codes the Google sign-in callback redirects back
// with (?oauth_error=). The detail is in the server logs only.
const OAUTH_ERROR_COPY = {
  not_allowed: 'That Google account is not a Wingman administrator.',
  account_disabled: 'This administrator account is not active.',
  unavailable: 'Google sign-in is temporarily unavailable. Please try again shortly.',
  auth_failed: "Google sign-in didn't complete. Please try again.",
  provider_unreachable: "Google sign-in didn't complete. Please try again.",
  expired: 'That sign-in expired before it finished. Please try again.',
};

function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [googleChecked, setGoogleChecked] = useState(false);
  // Lane 1426: false = admin sign-in is Google only (WINGMAN_ADMIN_PASSWORD_LOGIN_ENABLED).
  // Starts true so a failed config read keeps today's form; the server refuses anyway.
  const [passwordLoginEnabled, setPasswordLoginEnabled] = useState(true);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/oauth/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setGoogleEnabled(Boolean(data?.google_enabled)))
      .catch(() => {})
      .finally(() => setGoogleChecked(true));
    fetch(`${API_BASE}/api/sso/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setPasswordLoginEnabled(data?.admin_password_login_enabled !== false))
      .catch(() => {});
    const oauthError = new URLSearchParams(location.search).get('oauth_error');
    if (oauthError) {
      setError(OAUTH_ERROR_COPY[oauthError] || OAUTH_ERROR_COPY.auth_failed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Always login as admin type
      const result = await login(email, password, 'admin');

      if (result.success) {
        if (result.user.user_type === 'admin') {
          navigate('/admin');
        } else {
          setError('This login is for platform administrators only.');
        }
      } else {
        setError(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="portal-admin" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${colors.deepBlue} 0%, #1a1a2e 50%, ${colors.adminPurple}40 100%)`,
      padding: '20px',
    }}>
      <div style={{
        backgroundColor: colors.white,
        borderRadius: '16px',
        boxShadow: 'var(--shadow-lg)',
        width: '100%',
        maxWidth: '420px',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
          padding: '32px',
          textAlign: 'center',
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '14px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <WingmanLogo size={32} fill="white" />
          </div>
          <h1 style={{
            color: colors.white,
            fontSize: 'var(--font-size-hero)',
            fontWeight: 'var(--font-weight-bold)',
            margin: '0 0 8px 0',
          }}>
            Wingman
          </h1>
          <p style={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: 'var(--font-size-base)',
            margin: 0,
          }}>
            Platform administration access
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
          {error && (
            <div style={{
              backgroundColor: `${colors.danger}15`,
              border: `1px solid ${colors.danger}30`,
              color: colors.danger,
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '24px',
              fontSize: 'var(--font-size-base)',
            }}>
              {error}
            </div>
          )}

          {googleEnabled && (
            <>
              <button
                type="button"
                onClick={() => { window.location.href = `${API_BASE}/api/admin/oauth/start`; }}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: colors.white,
                  color: colors.darkText,
                  border: `2px solid ${colors.mediumGray}`,
                  borderRadius: '10px',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-medium)',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                }}
              >
                {/* Google "G" mark */}
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/>
                </svg>
                Sign in with Google
              </button>
              {passwordLoginEnabled && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0 16px' }}>
                  <div style={{ flex: 1, height: '1px', backgroundColor: colors.mediumGray }} />
                  <span style={{ color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>or</span>
                  <div style={{ flex: 1, height: '1px', backgroundColor: colors.mediumGray }} />
                </div>
              )}
            </>
          )}

          {!passwordLoginEnabled && !googleEnabled && googleChecked && (
            <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-base)', margin: 0, textAlign: 'center' }}>
              Sign-in is temporarily unavailable. Please try again shortly.
            </p>
          )}

          {passwordLoginEnabled && (<>
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'var(--font-weight-medium)',
              fontSize: 'var(--font-size-base)',
              color: colors.darkText,
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              required
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: `2px solid ${colors.mediumGray}`,
                borderRadius: '10px',
                fontSize: 'var(--font-size-base)',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = colors.adminPurple}
              onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'var(--font-weight-medium)',
              fontSize: 'var(--font-size-base)',
              color: colors.darkText,
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: `2px solid ${colors.mediumGray}`,
                borderRadius: '10px',
                fontSize: 'var(--font-size-base)',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = colors.adminPurple}
              onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
              color: colors.white,
              border: 'none',
              borderRadius: '10px',
              fontSize: 'var(--font-size-md)',
              fontWeight: 'var(--font-weight-semibold)',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${colors.adminPurple}50`;
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
          </>)}
        </form>

        {/* Footer */}
      </div>
    </div>
  );
}

export default AdminLogin;
